import { mapProposalToResumePremium } from '@/features/proposals/components/capital-global/utils/map-proposal-to-resume-premium.util';
import {
  getCapitalGlobalProposalAttachmentDisplayFileName,
  getCapitalGlobalProposalAttachmentSlots,
  hasCapitalGlobalSignedProposalAttachment,
  resolveCapitalGlobalProposalInsuranceKind,
  validateCapitalGlobalProposalAttachmentFile,
} from '@/features/proposals/components/capital-global/utils/capital-global-proposal-attachment.util';
import type { CapitalGlobalProposalStepProps } from '@/features/proposals/components/capital-global/capital-global-proposal-step.types';
import { isCapitalGlobalPhysicalSignatureUploadPending } from '@/features/proposals/components/capital-global/utils/capital-global-proposal-editability.util';
import {
  mapAttachmentsByDocumentType,
  useCapitalGlobalProposalAttachments,
  useRegisterCapitalGlobalProposalAttachment,
  useSoftDeleteCapitalGlobalProposalAttachment,
} from '@/features/proposals/hooks/capital-global/use-capital-global-proposal-attachments.hook';
import { useCapitalGlobalProposalInstallmentTypes } from '@/features/proposals/hooks/capital-global/use-capital-global-proposal-installment-types.hook';
import { useCapitalGlobalProposalPaymentTypes } from '@/features/proposals/hooks/capital-global/use-capital-global-proposal-payment-types.hook';
import { getProposalCategory } from '@/features/proposals/utils/proposal-category';
import {
  capitalGlobalProposalQueryKey,
  useCapitalGlobalDocusignSignatureStatus,
} from '@/features/proposals/hooks/capital-global/use-capital-global-docusign-signature.hook';
import { useCapitalGlobalProposalSignatureMethod } from '@/features/proposals/hooks/capital-global/use-capital-global-proposal-signature-method.hook';
import { toaster } from '@/shared/ui/toaster.component';
import type {
  CapitalGlobalProposalAttachment,
  CapitalGlobalProposalAttachmentDocumentType,
} from '@/services/proposals/types';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

type Args = Pick<
  CapitalGlobalProposalStepProps,
  'proposal' | 'policyId' | 'isViewMode'
>;

export function useCapitalGlobalProposalStep4ViewModel({
  proposal,
  policyId,
  isViewMode,
}: Args) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const trimmedPolicyId = policyId.trim();
  const [uploadingDocumentType, setUploadingDocumentType] =
    useState<CapitalGlobalProposalAttachmentDocumentType | null>(null);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<
    string | null
  >(null);
  const [exitAttempted, setExitAttempted] = useState(false);

  const schemaCategory = useMemo(
    () => getProposalCategory(proposal.productCode),
    [proposal.productCode]
  );

  const { paymentTypes } = useCapitalGlobalProposalPaymentTypes();
  const { premiumPeriodicityOptions: premiumPeriodicityCatalog } =
    useCapitalGlobalProposalInstallmentTypes();

  const premiumData = useMemo(
    () =>
      mapProposalToResumePremium(
        proposal,
        schemaCategory,
        paymentTypes,
        premiumPeriodicityCatalog
      ),
    [proposal, schemaCategory, paymentTypes, premiumPeriodicityCatalog]
  );

  const insuranceKind = useMemo(
    () => resolveCapitalGlobalProposalInsuranceKind(proposal),
    [proposal]
  );

  const slots = useMemo(
    () => getCapitalGlobalProposalAttachmentSlots(insuranceKind),
    [insuranceKind]
  );

  const attachmentsQuery = useCapitalGlobalProposalAttachments(trimmedPolicyId);
  const signatureQuery = useCapitalGlobalDocusignSignatureStatus(trimmedPolicyId);
  const signatureMethodQuery =
    useCapitalGlobalProposalSignatureMethod(trimmedPolicyId);
  const registerMutation =
    useRegisterCapitalGlobalProposalAttachment(trimmedPolicyId);
  const deleteMutation =
    useSoftDeleteCapitalGlobalProposalAttachment(trimmedPolicyId);

  const attachmentsByType = useMemo(
    () => mapAttachmentsByDocumentType(attachmentsQuery.data ?? []),
    [attachmentsQuery.data]
  );

  const hasSignedProposal = useMemo(
    () => hasCapitalGlobalSignedProposalAttachment(attachmentsByType),
    [attachmentsByType]
  );

  const isPhysicalUploadPending = useMemo(
    () =>
      isCapitalGlobalPhysicalSignatureUploadPending({
        signature: signatureQuery.data ?? null,
        signatureMethod: signatureMethodQuery.data ?? null,
        hasSignedProposal,
      }),
    [hasSignedProposal, signatureMethodQuery.data, signatureQuery.data]
  );

  const isUploadEnabled = !isViewMode || isPhysicalUploadPending;

  const showSignedProposalRequiredError =
    exitAttempted && !hasSignedProposal && isUploadEnabled;

  const handleFileSelect = useCallback(
    async (
      documentType: CapitalGlobalProposalAttachmentDocumentType,
      file: File
    ) => {
      if (!isUploadEnabled) return;

      const validationError = validateCapitalGlobalProposalAttachmentFile(file);
      if (validationError) {
        toaster.create({
          type: 'error',
          title: 'Arquivo inválido',
          description: validationError,
          duration: 5000,
        });
        return;
      }

      if (!trimmedPolicyId) {
        toaster.create({
          type: 'error',
          title: 'Erro',
          description: 'ID da proposta não encontrado.',
          duration: 5000,
        });
        return;
      }

      setUploadingDocumentType(documentType);

      try {
        await registerMutation.mutateAsync({ documentType, file });
        if (documentType === 'signed_proposal') {
          void queryClient.invalidateQueries({
            queryKey: capitalGlobalProposalQueryKey(trimmedPolicyId),
          });
          toaster.create({
            type: 'success',
            title: 'Proposta transmitida',
            description:
              'A proposta assinada foi registrada e marcada como transmitida.',
            duration: 5000,
          });
        } else {
          toaster.create({
            type: 'success',
            title: 'Arquivo enviado',
            description: `${file.name} foi registrado com sucesso.`,
            duration: 4000,
          });
        }
      } catch (error) {
        toaster.create({
          type: 'error',
          title: 'Erro ao enviar arquivo',
          description:
            error instanceof Error
              ? error.message
              : 'Não foi possível enviar o arquivo.',
          duration: 5000,
        });
      } finally {
        setUploadingDocumentType(null);
      }
    },
    [isUploadEnabled, queryClient, registerMutation, trimmedPolicyId]
  );

  const handleRemove = useCallback(
    async (attachment: CapitalGlobalProposalAttachment) => {
      if (!isUploadEnabled) return;

      setDeletingAttachmentId(attachment.id);

      try {
        await deleteMutation.mutateAsync(attachment.id);
        const displayFileName =
          getCapitalGlobalProposalAttachmentDisplayFileName(
            attachment.fileName,
            attachment.keyPath
          );
        toaster.create({
          type: 'success',
          title: 'Arquivo removido',
          description: `${displayFileName} foi excluído.`,
          duration: 4000,
        });
      } catch (error) {
        toaster.create({
          type: 'error',
          title: 'Erro ao excluir arquivo',
          description:
            error instanceof Error
              ? error.message
              : 'Não foi possível excluir o arquivo.',
          duration: 5000,
        });
      } finally {
        setDeletingAttachmentId(null);
      }
    },
    [deleteMutation, isUploadEnabled]
  );

  const handleDownload = useCallback(
    (attachment: CapitalGlobalProposalAttachment) => {
      if (!attachment.downloadUrl) {
        toaster.create({
          type: 'error',
          title: 'Download indisponível',
          description: 'Não foi possível obter o link de download.',
          duration: 5000,
        });
        return;
      }

      const anchor = document.createElement('a');
      anchor.href = attachment.downloadUrl;
      anchor.download = getCapitalGlobalProposalAttachmentDisplayFileName(
        attachment.fileName,
        attachment.keyPath
      );
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.click();
    },
    []
  );

  const handleExit = useCallback(() => {
    if (isViewMode && !isPhysicalUploadPending) {
      void navigate({ to: '/proposals' });
      return;
    }

    if (!hasSignedProposal) {
      setExitAttempted(true);
      toaster.create({
        type: 'error',
        title: 'Documento obrigatório',
        description:
          'Envie a Proposta de Seguro Assinada em PDF antes de sair.',
        duration: 5000,
      });
      return;
    }

    void navigate({ to: '/proposals' });
  }, [hasSignedProposal, isPhysicalUploadPending, isViewMode, navigate]);

  return {
    premiumData,
    isHydrating:
      attachmentsQuery.isLoading ||
      signatureQuery.isLoading ||
      signatureMethodQuery.isLoading,
    slots,
    attachmentsByType,
    uploadingDocumentType,
    deletingAttachmentId,
    showSignedProposalRequiredError,
    isUploadEnabled,
    isPhysicalUploadPending,
    handleFileSelect,
    handleRemove,
    handleDownload,
    handleExit,
  };
}
